// Copyright (c) StrangeBee 2024

import React, { ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { classList, useLocalMessage } from '@Helpers/';
import RouteBuilder from '@Services/RouteBuilder';
import { TaskOutput, TaskStatus } from 'thehive-sdk';
import { FormatDate } from '@Molecules/Format';
import { TaskDisplayStatus } from '../TaskDisplayStatus/TaskDisplayStatus';
import { ColorLabel } from '@Atoms/Info/ColorLabel/ColorLabel';
import { ICONS } from '@Constants/iconsApp';
import { PreviewButton } from '@Atoms/Buttons';

import './TaskListTitle.scss';

interface TaskListTitleProps {
    task: TaskOutput;
    caseId?: string;
    onDueDateClick?: (dueDate: number) => void;
    onGroupClick?: (group: string) => void;
    onStatusClick?: (status: string) => void;
    showCaseInfo?: boolean;
    onPreviewClick?: () => void;
    inline?: boolean;
    disabled?: boolean;
}

const TaskListTitle = ({
    task,
    caseId,
    onDueDateClick,
    onGroupClick,
    onStatusClick,
    onPreviewClick,
    inline,
    disabled,
}: TaskListTitleProps): ReactElement => {
    const i = useLocalMessage();

    const { _id: taskId, title, startDate = 0, endDate = 0, status, dueDate = 0, group = '' } = task;

    return (
        <div className="task-title">
            <div
                className={classList('task-title__header', {
                    inline,
                })}
            >
                <div className="task-title__info">
                    <ColorLabel
                        color="primary-color"
                        label={group}
                        type="outline"
                        onClick={() => onGroupClick?.(group)}
                    />

                    {caseId ? (
                        <Link to={RouteBuilder.compute('cases.task', { caseId, taskId })} className="task-title__link">
                            {title}
                        </Link>
                    ) : (
                        title
                    )}
                </div>

                <div className="task-title__group-date">
                    {(!!startDate || !!endDate || status === TaskStatus.CANCEL) && (
                        <div className="task-title__date">
                            {status !== TaskStatus.WAITING && (
                                <TaskDisplayStatus status={status} onClick={() => onStatusClick?.(status)} />
                            )}

                            {status === TaskStatus.IN_PROGRESS && !!startDate && (
                                <span className="start">
                                    <FormatDate date={startDate} onlyFrom label={i('general.started')} />
                                </span>
                            )}

                            {status === TaskStatus.CANCEL && (
                                <span className="cancel">
                                    {!endDate ? (
                                        i('general.canceled')
                                    ) : (
                                        <FormatDate date={endDate} onlyFrom label={i('general.canceled')} />
                                    )}
                                </span>
                            )}

                            {status === TaskStatus.COMPLETED && (
                                <span className="end">
                                    <FormatDate date={endDate} onlyFrom label={i('general.closed')} />
                                </span>
                            )}
                        </div>
                    )}

                    {!!dueDate && status !== TaskStatus.COMPLETED && (
                        <div
                            className={classList('task-title__due-date', {
                                before: dueDate > new Date().valueOf(),
                                after: dueDate <= new Date().valueOf(),
                                clickable: !!onDueDateClick && !disabled,
                            })}
                            onClick={(): void => onDueDateClick?.(dueDate)}
                        >
                            <ICONS.ClockCircleOutlined />
                            <FormatDate date={dueDate} onlyFrom label={i('common.due-date')} />
                        </div>
                    )}
                </div>
            </div>

            {onPreviewClick && (
                <div className="generic-drawer-preview">
                    <PreviewButton onClick={onPreviewClick} />
                </div>
            )}
        </div>
    );
};

export default TaskListTitle;
